
/**
 * A simple circuit-breaker: trips to OPEN on too many failures,
 * half-opens after a timeout, then closes if a call succeeds.
 */
export type CircuitState = "CLOSED" | "HALF_OPEN" | "OPEN";

export interface CircuitBreakerOptions {
  /** how many failures before we "open" the breaker */
  maxFailures?: number;
  /** alias for maxFailures, to satisfy existing callers */
  failureThreshold?: number;
  /** how long to wait (ms) before trying a half-open retry */
  resetTimeout?: number;
  /** Optional function to call when circuit is OPEN */
  fallbackFunction?: (toolName: string, method: string, params: any) => Promise<any>;
}

// Simple event handling implementation for browser environments
type EventHandler = (...args: any[]) => void;

class BrowserEventEmitter {
  private eventHandlers: Record<string, EventHandler[]> = {};

  on(event: string, handler: EventHandler): this {
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = [];
    }
    this.eventHandlers[event].push(handler);
    return this;
  }

  emit(event: string, ...args: any[]): boolean {
    const handlers = this.eventHandlers[event];
    if (!handlers || handlers.length === 0) {
      return false;
    }
    
    handlers.forEach(handler => handler(...args));
    return true;
  }

  removeListener(event: string, handler: EventHandler): this {
    const handlers = this.eventHandlers[event];
    if (handlers) {
      this.eventHandlers[event] = handlers.filter(h => h !== handler);
    }
    return this;
  }
}

export class CircuitBreaker extends BrowserEventEmitter {
  private state: CircuitState = "CLOSED";
  private failCount: number = 0;
  private openedAt: number = 0;
  private maxFailures: number;
  private resetTimeout: number;
  private fallbackFunction?: (toolName: string, method: string, params: any) => Promise<any>;

  constructor({
    failureThreshold,
    maxFailures = 3,
    resetTimeout = 30000,
    fallbackFunction
  }: CircuitBreakerOptions = {}) {
    super();
    // Support both failureThreshold and maxFailures
    this.maxFailures = failureThreshold ?? maxFailures;
    this.resetTimeout = resetTimeout;
    this.fallbackFunction = fallbackFunction;
  }

  private transitionToOpen(): void {
    this.state = "OPEN";
    this.openedAt = Date.now();
    this.emit("open");
  }

  /** before each call: if OPEN and timeout elapsed, go to HALF_OPEN */
  private maybeHalfOpen(): void {
    if (this.state === "OPEN" && Date.now() > (this.openedAt + this.resetTimeout)) {
      this.state = "HALF_OPEN";
      this.emit("half_open");
    }
  }

  /** on success: reset back to CLOSED */
  private succeed(): void {
    this.failCount = 0;
    this.state = "CLOSED";
    this.emit("close");
  }

  async execute<T>(
    fn: () => Promise<T>,
    toolName: string,
    method: string,
    params: any
  ): Promise<T> {
    this.maybeHalfOpen();

    if (this.state === "OPEN") {
      if (this.fallbackFunction) {
        return this.fallbackFunction(toolName, method, params);
      }
      throw new Error(`Circuit breaker is OPEN for ${toolName}.${method}`);
    }

    try {
      const result = await fn();
      this.succeed();
      return result;
    } catch (err: any) {
      this.failCount++;
      this.emit("failure", err);

      if (this.failCount >= this.maxFailures) {
        this.transitionToOpen();
      }
      throw err;
    }
  }
}
